'use client';

import { useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { toImageItems } from '@/lib/media';
import { SmartImg } from './Skeleton';
import Lightbox from './Lightbox';

interface PairGalleryProps {
  images: string[];
  label: string;
}

/**
 * A staggered pair of image cards — the second sits lower than the first — that
 * open in the shared Lightbox (used for Floors & Doors: Doors + Floor). Hovering
 * a card zooms its image. The zoom lives on a wrapper <span> so its transform
 * transition never fights SmartImg's own opacity fade-in. Plain <img> with
 * encodeURI'd src (paths contain spaces/parens).
 */
export default function PairGallery({ images, label }: PairGalleryProps) {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const reduceMotion = useReducedMotion();

  return (
    <>
      <div className="grid grid-cols-2 items-start gap-6">
        {images.map((src, i) => (
          // Plank reveal: each card is "laid" left-to-right like a floorboard,
          // one after the other (this gallery backs Floors & Doors).
          <motion.button
            key={src}
            type="button"
            initial={reduceMotion ? { opacity: 0 } : { clipPath: 'inset(0 100% 0 0)' }}
            whileInView={
              reduceMotion ? { opacity: 1 } : { clipPath: 'inset(0 0% 0 0)' }
            }
            viewport={{ once: true, margin: '-10% 0px' }}
            transition={{ duration: 0.65, delay: i * 0.18, ease: [0.23, 1, 0.32, 1] }}
            onClick={() => {
              setIndex(i);
              setOpen(true);
            }}
            aria-label={`${label} ${i + 1} — open`}
            className={`forest-img-shadow group relative aspect-[3/4] overflow-hidden rounded-sm border border-surface-bright/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
              i % 2 === 1 ? 'mt-10 sm:mt-16 lg:mt-24' : ''
            }`}
          >
            {/* Zoom on a wrapper (direct hover, not group-hover) so transform doesn't
                collide with the image's fade and doesn't depend on the group marker. */}
            <span className="block h-full w-full transition-transform duration-500 ease-out hover:scale-[1.3]">
              <SmartImg
                src={encodeURI(src)}
                alt={`${label} ${i + 1}`}
                className="h-full w-full object-cover object-center"
              />
            </span>
          </motion.button>
        ))}
      </div>

      <Lightbox
        items={toImageItems(images)}
        index={index}
        open={open}
        label={label}
        onClose={() => setOpen(false)}
        onIndexChange={setIndex}
      />
    </>
  );
}
